/**
 * Handlebars partial templates for the receipt template.
 * Each partial isolates a country-specific section of the receipt,
 * keeping the main ReceiptTemplate clean and maintainable.
 *
 * Dev-preview .hbs mirrors live under src/printing/templates/receipt/partials/
 */

/** Company/group display name — DE uses companyName, ES uses displayName, others use name */
export const HeaderNamePartial = `{{#if germanyAdditionalInfo}}
    {{ groupInfo.companyName }}
{{else}}
{{#if spainAdditionalInfo}}
    {{ groupInfo.displayName }}
{{else}}
    {{ groupInfo.name }}
{{/if}}
{{/if}}`;

/** Business identifier — CIF for Spain, VAT/registration for others */
export const HeaderBusinessIdPartial = `<div>
  {{#if spainAdditionalInfo}}
    CIF: {{groupInfo.cif}}
  {{else}}
  {{#if belgiumAdditionalInfo}}
    {{t "field.business-number"}}: {{ groupInfo.vatId }}
  {{else}}
  {{#if groupInfo.vatNumber }}
    {{t "field.business-number"}}: {{ groupInfo.vatNumber }}
  {{else}}
    {{t "field.business-number"}}: {{ groupInfo.registrationNum }}
  {{/if}}
  {{/if}}
  {{/if}}
</div>

{{#if belgiumAdditionalInfo.locationCbe }}
<div>
  {{t "field.establishment-unit"}}: {{belgiumAdditionalInfo.locationCbe}}
</div>
{{/if}}

{{#if groupInfo.exoNumber }}
<div>
  EXO: {{groupInfo.exoNumber}}
</div>
{{/if}}`;

/** Reprint/copy/bill labels — country-specific reprint indicators */
export const HeaderReprintLabelsPartial = `{{#if austriaAdditionalInfo.receiptType}}
  <div class="f-bold">{{t "receipt.commercial-doc"}}</div>
  <div class="f-bold mb-title">{{austriaAdditionalInfo.receiptType}}</div>
{{/if}}
{{#if italyAdditionalInfo.receiptType}}
  <div class="f-bold">{{t "receipt.commercial-doc"}}</div>
  <div class="f-bold mb-title">{{italyAdditionalInfo.receiptType}}</div>
{{/if}}

{{#if isReceiptCopy}}
  <div class="f-bold">{{t "becert.copy"}}</div>
{{/if}}
{{#if germanyAdditionalInfo.isReprint}}
  <div class="f-bold mb-title">{{t "becert.copy"}}</div>
{{/if}}
{{#if spainAdditionalInfo.isReprint}}
  <div class="f-bold mb-title">{{t "becert.copy"}}</div>
{{/if}}
{{#if croatiaAdditionalInfo.isReprint}}
  <div class="f-bold mb-title">{{t "becert.copy"}}</div>
{{/if}}
{{!-- BE: only an original sale is a valid VAT receipt; a copy is not --}}
{{#if belgiumAdditionalInfo.isValidVatReceipt}}
  <div class="f-bold mb-5">{{t "becert.vat-receipt"}}</div>
  {{#if belgiumAdditionalInfo.isFullCancellation}}
  <div class="f-bold mb-title">{{t "becert.refunded"}}</div>
  {{/if}}
{{/if}}
{{#if belgiumAdditionalInfo.isReprint}}
  <div class="f-bold mb-title">{{t "becert.copy"}}</div>
{{/if}}
{{#if isBill}}
  <div class="f-bold mb-title">{{t "receipt.bill"}}</div>
{{/if}}`;

/** France fiscal header — NF525 compliance fields (NAF, SIRET, SIREN, etc.) */
export const ReceiptInfoFrancePartial = `{{#if franceAdditionalInfo}}
{{#if isSignatureRequired}}
{{#if isSigned}}
{{#if franceAdditionalInfo.signature}}
{{#if franceAdditionalInfo.fiscalNumber}}
  <div class="france-header">{{t "receipt.fiscal-number"}}: {{franceAdditionalInfo.fiscalNumber}}</div>
{{/if}}
{{#if franceAdditionalInfo.originalFiscalNumber}}
  <div class="france-header">{{t "receipt.original-fiscal-number"}}: {{franceAdditionalInfo.originalFiscalNumber}}</div>
{{/if}}
{{/if}}
{{/if}}
{{/if}}
  <div class="france-header">NAF: {{franceAdditionalInfo.nafCode}} - SIRET: {{franceAdditionalInfo.siret}}</div>
  <div class="france-header">{{t "field.siren"}}: {{franceAdditionalInfo.siren}}</div>
  <div class="france-header">{{t "field.social-capital"}}: {{franceAdditionalInfo.socialCapital}}</div>
  <div class="france-header">{{t "field.legal-form"}}: {{franceAdditionalInfo.legalForm}}</div>
  <div class="france-header">{{t "field.document-type-x-operation"}}: {{franceAdditionalInfo.documentTypexOperation}}</div>
  <div class="france-header">{{#if franceAdditionalInfo.isReprint}}{{t "field.sw-version-current"}}{{else}}{{t "field.sw-version"}}{{/if}}: {{franceAdditionalInfo.swVersion}}</div>
  {{#if franceAdditionalInfo.isReprint}}
  <div class="france-header">{{t "field.sw-version-original"}}: {{franceAdditionalInfo.swVersionOriginal}}</div>
  {{/if}}
  <div class="france-header">{{#if franceAdditionalInfo.isReprint}}{{t "field.pos-id-current"}}{{else}}{{t "field.pos-id"}}{{/if}}: {{franceAdditionalInfo.posId}}</div>
  <div class="france-header">{{t "field.line-count"}}: {{franceAdditionalInfo.lineCount}}</div>
  <div class="france-header">{{#if franceAdditionalInfo.isReprint}}{{t "field.reprint-count"}}{{else}}{{t "field.print-count"}}{{/if}}: {{franceAdditionalInfo.printCount}}</div>
  {{#if franceAdditionalInfo.clientId}}
  <div class="france-header">{{t "field.client-id"}}: {{franceAdditionalInfo.clientId}}</div>
  <div class="france-header">{{t "field.client"}}: {{franceAdditionalInfo.client}}</div>
  {{/if}}
  {{#if franceAdditionalInfo.isReprint}}
  <div class="france-header">{{t "field.original-print-timestamp"}}: {{ date }} {{ hour }}</div>
  <div class="france-header">{{t "field.reprint-timestamp"}}: {{franceAdditionalInfo.reprintTimestamp}}</div>
  <div class="france-header">{{t "field.seller-name-current"}}: {{franceAdditionalInfo.sellerName}}</div>
  {{#if franceAdditionalInfo.originalSellerName}}
  <div class="france-header">{{t "field.seller-name-orignal"}}: {{franceAdditionalInfo.originalSellerName}}</div>
  {{/if}}
  {{/if}}
{{/if}}`;

/**
 * Croatia fiscal info block — all Fis.Tag elements required by Croatian law.
 * Printed in the receipt-info section (before products).
 * OIB + DATUM + FISCAL RAČUN always shown; ZKI/JIR/PKP only when signed.
 */
export const ReceiptInfoCroatiaPartial = `{{#if croatiaAdditionalInfo}}
{{#if isSignatureRequired}}
{{#if croatiaAdditionalInfo.oib}}
  <div class="d-flex fl-jsb mb-10">
    <div>OIB:</div>
    <div><span class="f-bold">{{croatiaAdditionalInfo.oib}}</span></div>
  </div>
{{/if}}
{{#if croatiaAdditionalInfo.timestamp}}
  <div class="d-flex fl-jsb mb-10">
    <div>{{t "dashboards.date"}}:</div>
    <div><span class="f-bold">{{croatiaAdditionalInfo.timestamp}}</span></div>
  </div>
{{/if}}
{{#if croatiaAdditionalInfo.fiscalNumber}}
  <div class="d-flex fl-jsb mb-10">
    <div>{{t "receipt.fiscal-receipt"}}:</div>
    <div><span class="f-bold">{{croatiaAdditionalInfo.fiscalNumber}}</span></div>
  </div>
{{/if}}
{{#if isSigned}}
{{#if croatiaAdditionalInfo.zki}}
  <div class="d-flex fl-jsb mb-10">
    <div>ZKI:</div>
    <div style="word-break:break-all;max-width:60%;text-align:right;"><span class="f-bold">{{croatiaAdditionalInfo.zki}}</span></div>
  </div>
{{/if}}
{{#if croatiaAdditionalInfo.jir}}
  <div class="d-flex fl-jsb mb-10">
    <div>JIR:</div>
    <div style="word-break:break-all;max-width:60%;text-align:right;"><span class="f-bold">{{croatiaAdditionalInfo.jir}}</span></div>
  </div>
{{else}}
{{#if croatiaAdditionalInfo.pkp}}
  <div class="d-flex fl-jsb mb-10">
    <div>PKP:</div>
    <div style="word-break:break-all;max-width:60%;text-align:right;"><span class="f-bold">{{croatiaAdditionalInfo.pkp}}</span></div>
  </div>
{{/if}}
{{/if}}
{{/if}}
{{/if}}
{{/if}}`;

/** Austria fiscal header — POS ID and Fiskaly Register ID */
export const ReceiptInfoAustriaPartial = `{{!-- Austria fiscal header — POS ID, Fiskaly Register ID and receipt number --}}
{{#if austriaAdditionalInfo}}
    {{#if austriaAdditionalInfo.receiptNumber}}
        <div class="d-flex fl-jsb mb-10">
            <div>{{t "receipt.receipt-number"}}:</div>
            <div> <span class="f-bold">{{ austriaAdditionalInfo.receiptNumber }}</span></div>
        </div>
    {{/if}}
    <div class="d-flex fl-jsb mb-10">
        <div>{{t "field.pos-id"}}:</div>
        <div> <span class="f-bold">{{ austriaAdditionalInfo.posId }}</span></div>
    </div>
    <div class="d-flex fl-jsb mb-10">
        <div>{{t "field.fiskaly-register-id"}}:</div>
        <div> <span class="f-bold">{{ austriaAdditionalInfo.fiskalyCashRegisterId }}</span></div>
    </div>
{{/if}}`;

/** Date/seller row — France reprint skips this (shown in fiscal header), Spain hides date (shown at bottom) */
export const DateSellerRowPartial = `{{#unless franceAdditionalInfo.isReprint}}
{{!-- BE prints POS date/time and the seller in its own block, so this row
      would duplicate them with a differently-formatted seller name --}}
{{#unless belgiumAdditionalInfo}}
<div class="d-flex fl-jsb mb-10 freg">
  {{#unless spainAdditionalInfo}}
  <div style="width: 55%;">{{ date }} {{ hour }}</div>
  {{/unless}}
  <div class="d-flex fl-col fl-ae f-reg">
    <div>{{ sellerName }}</div>
  </div>
</div>
{{/unless}}
{{/unless}}`;

/** Signature/fiscal footer — DE/AT QR, IT documentId, FR signature, ES QR Tributario */
export const SignatureFooterPartial = `{{#if isSignatureRequired}}
{{#if isSigned}}
{{!-- DE --}}
{{#if germanyAdditionalInfo.qrCode}}
<div class="qrcode-container">
  <div class="qrcode"><img src="{{ germanyAdditionalInfo.qrCode }}" /></div>
</div>
{{/if}}
{{!-- AT --}}
{{#if austriaAdditionalInfo.qrCode}}
<div class="qrcode-container">
  <div class="qrcode"><img src="{{ austriaAdditionalInfo.qrCode }}" /></div>
</div>
{{/if}}
{{!-- IT --}}
{{#if italyAdditionalInfo}}
<div class="f-center mb-10" style="display:flex;flex-direction:column;gap:5px">
  <p>{{ date }} {{ hour }}</p>
  <p>{{t "receipt.document-id"}} {{italyAdditionalInfo.documentId}}</p>
</div>
{{/if}}
{{!-- FR --}}
{{#if franceAdditionalInfo.signature}}
<div class="f-center mb-10" style="display:flex;flex-direction:column;gap:5px">
  <p>{{#if franceAdditionalInfo.isReprint}}{{t "field.original-print-timestamp"}}:{{/if}} {{ date }} {{ hour }}</p>
  {{#if franceAdditionalInfo.isReprint}}
  <p>{{t "field.reprint-timestamp"}}: {{franceAdditionalInfo.reprintTimestamp}}</p>
  {{/if}}
  {{#if franceAdditionalInfo.fiscalNumber}}
  <p>{{t "receipt.code"}}: {{franceAdditionalInfo.signature}}</p>
  {{#if franceAdditionalInfo.originalSignature}}
  <p>{{t "receipt.original-code"}}: {{franceAdditionalInfo.originalSignature}}</p>
  {{/if}}
  <p>{{t "receipt.fiscal-number"}}: {{franceAdditionalInfo.fiscalNumber}}</p>
  {{/if}}
  {{#if franceAdditionalInfo.originalFiscalNumber}}
  <p>{{t "receipt.original-fiscal-number"}}: {{franceAdditionalInfo.originalFiscalNumber}}</p>
  {{/if}}
</div>
{{/if}}
{{/if}}
{{!-- ES --}}
{{#if spainAdditionalInfo}}
<div class="f-center mb-10" style="display:flex;flex-direction:column;gap:5px">
  <p>{{t "receipt.fiscal-number"}}: {{spainAdditionalInfo.fiscalNumber}}</p>
  <p>{{spainAdditionalInfo.timestamp}}</p>
  <p>{{spainAdditionalInfo.signature}}</p>
</div>
<div class="qrcode-container">
  QR Tributario:
  <div class="qrcode">
  <img src="{{ spainAdditionalInfo.qrCode }}" /></div>
  VERI*FACTU
</div>
{{/if}}
{{!-- HR — QR code for porezna.gov.hr verification (all text labels are in receiptInfoCroatia) --}}
{{#if croatiaAdditionalInfo.qrCode}}
<div class="qrcode-container">
  <div class="qrcode"><img src="{{ croatiaAdditionalInfo.qrCode }}" /></div>
</div>
{{/if}}
{{!-- HR --}}

{{!-- BE --}}
{{#if belgiumAdditionalInfo}}
{{#if isSigned}}
{{#if belgiumAdditionalInfo.qrCode}}
<div class="qrcode-container">
  <div class="qrcode"><img src="{{ belgiumAdditionalInfo.qrCode }}" /></div>
</div>
{{/if}}
<div class="f-center mb-10" style="display:flex;flex-direction:column;gap:5px">
  <p>{{t "field.fdm-id"}}: {{belgiumAdditionalInfo.fdmId}}</p>
  <p>{{t "field.fdm-date-time"}}: {{belgiumAdditionalInfo.fdmDateTime}}</p>
  <p>{{t "field.ticket-counter"}}: {{belgiumAdditionalInfo.ticketCounter}}</p>
  {{#if belgiumAdditionalInfo.signature}}
  <p class="no-overflow">{{t "receipt.signature"}}: {{belgiumAdditionalInfo.signature}}</p>
  {{/if}}
  {{#if belgiumAdditionalInfo.fiscalNumber}}
  <p>{{t "receipt.fiscal-number"}}: {{belgiumAdditionalInfo.fiscalNumber}}</p>
  {{/if}}
  {{#if belgiumAdditionalInfo.includeOriginalFdmRef}}
  <p>{{t "field.original-fdm-id"}}: {{belgiumAdditionalInfo.originalFdmId}}</p>
  <p>{{t "field.original-fdm-date-time"}}: {{belgiumAdditionalInfo.originalFdmDateTime}}</p>
  <p>{{t "field.original-ticket-counter"}}: {{belgiumAdditionalInfo.originalTicketCounter}}</p>
  {{#if belgiumAdditionalInfo.originalSignature}}
  <p class="no-overflow">{{t "receipt.original-signature"}}: {{belgiumAdditionalInfo.originalSignature}}</p>
  {{/if}}
  {{#if belgiumAdditionalInfo.originalFiscalNumber}}
  <p>{{t "receipt.original-fiscal-number"}}: {{belgiumAdditionalInfo.originalFiscalNumber}}</p>
  {{/if}}
  {{/if}}
  {{#if belgiumAdditionalInfo.reprintTimestamp}}
  <p>{{t "field.reprint-timestamp"}}: {{belgiumAdditionalInfo.reprintTimestamp}}</p>
  {{/if}}
</div>
{{#if belgiumAdditionalInfo.footer}}
<p class="f-center mb-10">{{belgiumAdditionalInfo.footer}}</p>
{{/if}}
{{/if}}
{{/if}}

{{#unless isSigned}}
<p class="f-center freg mb-10">{{t "becert.transaction-not-signed"}}</p>
{{/unless}}

{{/if}}`;

export const ReceiptInfoBelgiumPartial = `{{#if belgiumAdditionalInfo}}
{{#if isSignatureRequired}}
<div class="belgium-header">{{t "field.sw-version"}}: {{belgiumAdditionalInfo.swVersion}}</div>
<div class="belgium-header">{{t "field.pos-id"}}: {{belgiumAdditionalInfo.posId}}</div>
<div class="belgium-header">{{t "field.terminal-id"}}: {{belgiumAdditionalInfo.terminalId}}</div>
<div class="belgium-header">{{t "field.device-id"}}: {{belgiumAdditionalInfo.deviceId}}</div>
<div class="belgium-header">{{t "field.transaction-number"}}: {{belgiumAdditionalInfo.transactionNumber}}</div>
{{#if belgiumAdditionalInfo.clientId}}
<div class="belgium-header">{{t "field.client-id"}}: {{belgiumAdditionalInfo.clientId}}</div>
<div class="belgium-header">{{t "field.client"}}: {{belgiumAdditionalInfo.client}}</div>
{{/if}}
<div class="belgium-header">{{t "field.seller-name"}}: {{belgiumAdditionalInfo.sellerName}} (ID: {{belgiumAdditionalInfo.sellerId}})</div>
<div class="belgium-header">{{t "field.pos-date-time"}}: {{date}} {{hour}}</div>
{{!-- the session id prints as a right-aligned row next to Ref, not here --}}
{{/if}}
{{/if}}`;
